import { Component, Input, Output, EventEmitter, OnChanges, ChangeDetectorRef, inject, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Newsletter, Blog, ContentStatus } from '../../types';
import { marked } from 'marked';

interface EditDraft {
    Subject: string;
    PreviewText: string;
    Body: string;
}

@Component({
    standalone: false,
    selector: 'mkt-newsletter-detail-panel',
    template: `
    <div class="mkt-detail-panel">
      <!-- Header -->
      <div class="mkt-detail-panel__header">
        <input *ngIf="editing && editDraft" class="mkt-detail-panel__title-input"
               [(ngModel)]="editDraft.Subject" placeholder="Newsletter subject" />
        <h3 *ngIf="!editing" class="mkt-detail-panel__title">
          <i class="fa-solid fa-envelope" style="margin-right: 6px; opacity: 0.5;"></i>
          {{ newsletter.Subject }}
        </h3>
        <div class="mkt-detail-panel__header-actions">
          <button *ngIf="!editing" class="mkt-detail-panel__edit-btn" (click)="enterEdit()" title="Edit">
            <i class="fa-solid fa-pencil"></i>
          </button>
          <button class="mkt-detail-panel__close" (click)="close.emit()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <!-- Status bar -->
      <div class="mkt-detail-panel__status-bar">
        <span class="mkt-detail-panel__status-badge" [class]="'mkt-detail-panel__status-badge--' + statusName.toLowerCase().replace(' ', '-')">
          {{ statusName }}
        </span>
        <span *ngIf="newsletter.ScheduledSendDate" class="mkt-detail-panel__meta-item">
          <i class="fa-regular fa-calendar"></i> Scheduled: {{ newsletter.ScheduledSendDate | date:'mediumDate' }}
        </span>
        <span *ngIf="newsletter.SentDate" class="mkt-detail-panel__meta-item">
          <i class="fa-solid fa-paper-plane"></i> Sent: {{ newsletter.SentDate | date:'mediumDate' }}
        </span>
        <span class="mkt-detail-panel__meta-item">
          <i class="fa-regular fa-clock"></i> Created: {{ newsletter.__mj_CreatedAt | date:'mediumDate' }}
        </span>
      </div>

      <!-- Content -->
      <div class="mkt-detail-panel__body">
        <!-- Edit mode -->
        <ng-container *ngIf="editing && editDraft">
          <div class="mkt-detail-panel__field">
            <label>Preview Text</label>
            <input [(ngModel)]="editDraft.PreviewText" placeholder="Preview text for email clients (90-140 chars)"
                   class="mkt-detail-panel__input" maxlength="140" />
            <span class="mkt-detail-panel__char-count">{{ editDraft.PreviewText?.length || 0 }}/140</span>
          </div>
          <div class="mkt-detail-panel__field">
            <label>Body</label>
            <textarea [(ngModel)]="editDraft.Body" rows="20"
                      class="mkt-detail-panel__textarea" placeholder="Newsletter body (markdown)"></textarea>
          </div>
          <div class="mkt-detail-panel__edit-actions">
            <button class="mkt-detail-panel__btn mkt-detail-panel__btn--primary" (click)="saveEdit()">Save</button>
            <button class="mkt-detail-panel__btn" (click)="cancelEdit()">Cancel</button>
          </div>
        </ng-container>

        <!-- View mode -->
        <ng-container *ngIf="!editing">
          <!-- Preview text -->
          <div *ngIf="newsletter.PreviewText" class="mkt-detail-panel__preview-text">
            <span class="mkt-detail-panel__label">Preview:</span> {{ newsletter.PreviewText }}
          </div>

          <!-- Linked blogs -->
          <div *ngIf="linkedBlogs.length > 0" class="mkt-detail-panel__linked-blogs">
            <div class="mkt-detail-panel__section-header">
              <i class="fa-solid fa-link"></i> Linked Blog Posts ({{ linkedBlogs.length }})
            </div>
            <div *ngFor="let blog of linkedBlogs; let i = index" class="mkt-detail-panel__linked-blog-item"
                 (click)="openBlog.emit(blog.ID)">
              <span class="mkt-detail-panel__linked-blog-order">{{ i + 1 }}.</span>
              <span class="mkt-detail-panel__linked-blog-title">{{ blog.Title }}</span>
              <i class="fa-solid fa-arrow-up-right-from-square" style="opacity: 0.4; font-size: 11px;"></i>
            </div>
          </div>

          <!-- Body content rendered -->
          <div class="mkt-detail-panel__section-header" style="margin-top: 16px;">
            <i class="fa-solid fa-file-lines"></i> Newsletter Body
          </div>
          <div class="mkt-detail-panel__content" [innerHTML]="renderedContent"></div>
        </ng-container>
      </div>

      <!-- Footer actions -->
      <div *ngIf="!editing" class="mkt-detail-panel__footer">
        <button *ngIf="statusName === 'Draft'"
                class="mkt-detail-panel__btn mkt-detail-panel__btn--primary"
                (click)="statusChange.emit({ newsletterId: newsletter.ID, newStatus: 'Approved' })">
          <i class="fa-solid fa-check"></i> Approve
        </button>
        <button *ngIf="statusName === 'Approved'"
                class="mkt-detail-panel__btn mkt-detail-panel__btn--disabled"
                disabled title="Email sending integration coming soon">
          <i class="fa-solid fa-paper-plane"></i> Send (coming soon)
        </button>
        <button class="mkt-detail-panel__btn mkt-detail-panel__btn--danger"
                (click)="deleteNewsletter.emit(newsletter.ID)">
          <i class="fa-solid fa-trash"></i> Delete
        </button>
      </div>
    </div>
  `,
    styles: [`
    .mkt-detail-panel { display: flex; flex-direction: column; height: 100%; background: var(--mj-bg-surface); }
    .mkt-detail-panel__header { display: flex; align-items: center; gap: 8px; padding: 16px 20px; border-bottom: 1px solid var(--mj-border-default); }
    .mkt-detail-panel__title { flex: 1; margin: 0; font-size: 18px; font-weight: 600; color: var(--mj-text-primary); }
    .mkt-detail-panel__title-input { flex: 1; font-size: 18px; font-weight: 600; border: 1px solid var(--mj-border-default); border-radius: 6px; padding: 6px 10px; background: var(--mj-bg-page); color: var(--mj-text-primary); }
    .mkt-detail-panel__header-actions { display: flex; gap: 4px; }
    .mkt-detail-panel__edit-btn, .mkt-detail-panel__close { background: none; border: none; cursor: pointer; padding: 6px 8px; border-radius: 4px; color: var(--mj-text-secondary); }
    .mkt-detail-panel__edit-btn:hover, .mkt-detail-panel__close:hover { background: var(--mj-bg-page); color: var(--mj-text-primary); }

    .mkt-detail-panel__status-bar { display: flex; align-items: center; gap: 12px; padding: 10px 20px; background: var(--mj-bg-page); border-bottom: 1px solid var(--mj-border-default); font-size: 13px; color: var(--mj-text-secondary); flex-wrap: wrap; }
    .mkt-detail-panel__status-badge { padding: 2px 8px; border-radius: 10px; font-size: 12px; font-weight: 600; background: var(--mj-bg-page); color: var(--mj-text-secondary); }
    .mkt-detail-panel__status-badge--draft { background: var(--mj-status-warning-bg); color: var(--mj-status-warning); }
    .mkt-detail-panel__status-badge--approved { background: var(--mj-status-success-bg); color: var(--mj-color-success-400); }
    .mkt-detail-panel__status-badge--published { background: var(--mj-status-info-bg); color: var(--mj-color-info-500); }
    .mkt-detail-panel__status-badge--pending-review { background: color-mix(in srgb, var(--mj-color-violet-400) 15%, transparent); color: var(--mj-color-violet-400); }
    .mkt-detail-panel__meta-item { display: flex; align-items: center; gap: 4px; }

    .mkt-detail-panel__body { flex: 1; overflow-y: auto; padding: 20px; }
    .mkt-detail-panel__preview-text { padding: 10px 14px; background: var(--mj-bg-page); border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 14px; color: var(--mj-text-secondary); margin-bottom: 16px; }
    .mkt-detail-panel__label { font-weight: 600; color: var(--mj-text-primary); }
    .mkt-detail-panel__section-header { font-size: 13px; font-weight: 600; color: var(--mj-text-secondary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }

    .mkt-detail-panel__linked-blogs { margin-bottom: 16px; }
    .mkt-detail-panel__linked-blog-item { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 6px; cursor: pointer; font-size: 14px; color: var(--mj-text-primary); }
    .mkt-detail-panel__linked-blog-item:hover { background: var(--mj-bg-page); }
    .mkt-detail-panel__linked-blog-order { color: var(--mj-text-disabled); font-weight: 600; min-width: 20px; }
    .mkt-detail-panel__linked-blog-title { flex: 1; }

    .mkt-detail-panel__content { font-size: 15px; line-height: 1.7; color: var(--mj-text-primary); }
    .mkt-detail-panel__content h1, .mkt-detail-panel__content h2, .mkt-detail-panel__content h3 { margin-top: 1.2em; margin-bottom: 0.5em; }
    .mkt-detail-panel__content p { margin-bottom: 0.8em; }
    .mkt-detail-panel__content a { color: var(--mj-brand-primary); }

    .mkt-detail-panel__field { margin-bottom: 16px; }
    .mkt-detail-panel__field label { display: block; font-size: 13px; font-weight: 600; color: var(--mj-text-primary); margin-bottom: 4px; }
    .mkt-detail-panel__input { width: 100%; padding: 8px 10px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 14px; box-sizing: border-box; background: var(--mj-bg-page); color: var(--mj-text-primary); }
    .mkt-detail-panel__textarea { width: 100%; padding: 10px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-family: 'SF Mono', monospace; font-size: 13px; resize: vertical; box-sizing: border-box; background: var(--mj-bg-page); color: var(--mj-text-primary); }
    .mkt-detail-panel__char-count { font-size: 11px; color: var(--mj-text-disabled); margin-top: 2px; display: block; text-align: right; }
    .mkt-detail-panel__edit-actions { display: flex; gap: 8px; margin-top: 12px; }

    .mkt-detail-panel__footer { display: flex; gap: 8px; padding: 12px 20px; border-top: 1px solid var(--mj-border-default); }
    .mkt-detail-panel__btn { padding: 8px 16px; border: 1px solid var(--mj-border-default); border-radius: 6px; font-size: 13px; cursor: pointer; background: var(--mj-bg-surface); color: var(--mj-text-primary); display: flex; align-items: center; gap: 6px; }
    .mkt-detail-panel__btn:hover { background: var(--mj-bg-page); }
    .mkt-detail-panel__btn--primary { background: var(--mj-brand-primary); color: var(--mj-text-inverse); border-color: var(--mj-brand-primary); }
    .mkt-detail-panel__btn--primary:hover { filter: brightness(1.1); }
    .mkt-detail-panel__btn--danger { color: var(--mj-status-error); border-color: color-mix(in srgb, var(--mj-status-error) 30%, transparent); }
    .mkt-detail-panel__btn--danger:hover { background: var(--mj-status-error-bg); }
    .mkt-detail-panel__btn--disabled { opacity: 0.5; cursor: not-allowed; }
  `]
})
export class NewsletterDetailPanelComponent implements OnChanges {
    private cdr = inject(ChangeDetectorRef);
    private sanitizer = inject(DomSanitizer);

    @Input() newsletter!: Newsletter;
    @Input() linkedBlogs: Blog[] = [];
    @Input() contentStatuses: ContentStatus[] = [];

    @Output() close = new EventEmitter<void>();
    @Output() statusChange = new EventEmitter<{ newsletterId: string; newStatus: string }>();
    @Output() deleteNewsletter = new EventEmitter<string>();
    @Output() newsletterSaved = new EventEmitter<string>();
    @Output() openBlog = new EventEmitter<string>();

    editing = false;
    editDraft: EditDraft | null = null;
    renderedContent: string = '';

    get statusName(): string {
        if (!this.newsletter || !this.contentStatuses.length) return 'Unknown';
        const status = this.contentStatuses.find(s => s.ID === this.newsletter.ContentStatusID);
        return status?.Name || this.newsletter.ContentStatus || 'Unknown';
    }

    ngOnChanges(): void {
        this.renderBody();
    }

    private renderBody(): void {
        if (!this.newsletter?.Body) {
            this.renderedContent = '';
            return;
        }
        const html = marked.parse(this.newsletter.Body, { async: false }) as string;
        this.renderedContent = this.sanitizer.sanitize(SecurityContext.HTML, html) || '';
    }

    enterEdit(): void {
        this.editDraft = {
            Subject: this.newsletter.Subject,
            PreviewText: this.newsletter.PreviewText || '',
            Body: this.newsletter.Body,
        };
        this.editing = true;
    }

    cancelEdit(): void {
        this.editing = false;
        this.editDraft = null;
    }

    async saveEdit(): Promise<void> {
        if (!this.editDraft) return;
        this.newsletter.Subject = this.editDraft.Subject;
        this.newsletter.PreviewText = this.editDraft.PreviewText || null;
        this.newsletter.Body = this.editDraft.Body;
        const saved = await this.newsletter.Save();
        if (saved) {
            this.editing = false;
            this.editDraft = null;
            this.renderBody();
            this.newsletterSaved.emit(this.newsletter.ID);
        }
        this.cdr.detectChanges();
    }
}
